"use client"

import React, { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { TrendingUp, BarChart3, Filter } from "lucide-react"
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { useOvertimeReasonsAnalytics } from "@/hooks/useOvertimeReasonsAnalytics"
import ExportButton from "@/components/ui/ExportButton"
import { cn } from "@/lib/utils"
import { TimeRange } from "@/types/enums"
import { getRankBadgeClasses, getProgressBarColor, getWinnerStyling, getRankHoverEffects } from "@/utils/leaderboardUtils"

interface OvertimeReasonsLeaderboardProps {
  className?: string
  startDate?: string
  endDate?: string
}

interface OvertimeReasonsLeaderboardHandle {
  download: () => void;
}



const OvertimeReasonsLeaderboard = forwardRef<OvertimeReasonsLeaderboardHandle, OvertimeReasonsLeaderboardProps>(
  function OvertimeReasonsLeaderboard({ className, startDate, endDate }, ref) {
    const [selectedDepartment, setSelectedDepartment] = useState<string>(TimeRange.ALL);
    const { reasonsAnalytics, departments, isLoading, error } = useOvertimeReasonsAnalytics({ 
      selectedDepartment, 
      startDate, 
      endDate 
    });

    // Prepare leaderboard data
    const leaderboardData = useMemo(() => {
      if (!reasonsAnalytics?.length) return [];

      return reasonsAnalytics
        .slice(0, 5) // Top 5 only
        .map((item, index) => ({
          rank: index + 1,
          category: item.category,
          reason: item.reason,
          count: item.count,
          percentage: item.percentage,
          color: getRankBadgeClasses(index + 1)
        }));
    }, [reasonsAnalytics]);

    // Note: Color functions moved to utility file for better maintainability

    // Calculate filtered total
    const filteredTotal = useMemo(() => {
      return reasonsAnalytics.reduce((sum, item) => sum + item.count, 0);
    }, [reasonsAnalytics]);

    // Handle CSV download
    const handleDownload = () => {
      if (!leaderboardData.length) return;

      const headers = ["Rank", "Category", "Reason", "Count", "Percentage", "Department Filter"];
      const csvData = leaderboardData.map(item => [
        item.rank.toString(),
        item.category,
        item.reason,
        item.count.toString(),
        `${item.percentage.toFixed(1)}%`,
        selectedDepartment === TimeRange.ALL ? "All Departments" : selectedDepartment
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map(row => row.join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `overtime_reasons_leaderboard_${selectedDepartment}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    useImperativeHandle(ref, () => ({
      download: handleDownload
    }));

    return (
      <Card className={cn("border-0 shadow-sm", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-slate-100 rounded-lg">
                <BarChart3 className="h-4 w-4 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">Overtime Analysis</CardTitle>
                <CardDescription className="text-sm text-slate-600">Leading causes of overtime work</CardDescription>
              </div>
            </div>
            <ExportButton 
              onClick={handleDownload} 
              disabled={!leaderboardData.length || isLoading} 
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Department Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-3 w-3 text-slate-500" />
              <label className="text-xs font-medium text-slate-700">Filter by Department</label>
            </div>
            <Select
              value={selectedDepartment}
              onValueChange={setSelectedDepartment}
              disabled={isLoading || departments.length === 0}
            >
              <SelectTrigger className="w-full h-8 text-xs border-slate-200 focus:border-slate-400 focus:ring-slate-400">
                <SelectValue placeholder="Select department..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TimeRange.ALL}>All Departments</SelectItem>
                {departments.map((department) => (
                  <SelectItem key={department} value={department}>
                    {department}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="h-12 bg-slate-100 rounded-lg" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="h-48 flex flex-col items-center justify-center text-center">
              <div className="p-2 bg-red-50 rounded-full mb-3">
                <BarChart3 className="w-6 h-6 text-red-400" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">Unable to load data</p>
              <p className="text-xs text-slate-600 max-w-xs">{error}</p>
            </div>
          ) : leaderboardData.length === 0 ? (
            <div className="h-48 flex flex-col items-center justify-center text-center">
              <div className="p-2 bg-slate-100 rounded-full mb-3">
                <BarChart3 className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">No data available</p>
              <p className="text-xs text-slate-600 max-w-xs">
                {selectedDepartment === TimeRange.ALL 
                  ? "No overtime requests with reasons found"
                  : `No overtime requests found for ${selectedDepartment} department`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {leaderboardData.map((item) => (
                <div
                  key={`${item.category}-${item.reason}`}
                  className={cn(
                    "group relative p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300",
                    getRankHoverEffects(item.rank),
                    getWinnerStyling(item.rank)
                  )}
                >
                  <div className="flex items-center justify-between">
                    {/* Left side: Rank and content */}
                    <div className="flex items-center space-x-3 flex-1">
                      {/* Rank indicator */}
                      <div className={cn(
                        "flex items-center justify-center w-6 h-6 rounded-md font-semibold text-xs",
                        item.color
                      )}>
                        {item.rank}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 text-xs leading-tight mb-0.5">
                          {item.reason.length > 35 ? `${item.reason.substring(0, 35)}...` : item.reason}
                        </h3>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs px-1.5 py-0.5 border-slate-200 text-slate-600">
                            {item.category}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {item.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right side: Count */}
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-900">
                        {item.count}
                      </div>
                      <div className="text-xs text-slate-500">
                        cases
                      </div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-2">
                    <div className="w-full bg-slate-100 rounded-full h-1">
                      <div
                        className={cn(
                          "h-1 rounded-full transition-all duration-700 ease-out",
                          getProgressBarColor(item.rank)
                        )}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        {leaderboardData.length > 0 && (
          <CardFooter className="pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3 w-3 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">
                  Leading cause: {leaderboardData[0]?.percentage.toFixed(1)}% of all cases
                </span>
              </div>
              <div className="text-xs text-slate-500">
                {filteredTotal} total requests
                {selectedDepartment !== TimeRange.ALL && ` • ${selectedDepartment}`}
              </div>
            </div>
          </CardFooter>
        )}
      </Card>
    );
  }
);

export default OvertimeReasonsLeaderboard;
